import { useState } from "react";
import { Button } from "./ui/Button";
import { Icon } from "../app/Icon";
import { reportBug } from "../lib/reportBug";
// Plain <a> (not react-router <Link>) — an error boundary may render when the
// router itself is broken, so this component must not depend on Router context.

/** Friendly error screen shown by the route errorElement + ErrorBoundary. Lets
 *  the user send the error straight into the support/ticketing system. */
export function ErrorFallback({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error ?? "Unknown error");
  const stack = error instanceof Error ? error.stack : undefined;
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  async function send() {
    setState("sending");
    try { await reportBug(message, stack); setState("sent"); }
    catch { setState("failed"); }
  }

  return (
    <div className="mx-auto grid min-h-[60vh] max-w-lg content-center justify-items-center gap-4 p-8 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-danger/10 text-danger"><Icon name="security" className="h-7 w-7" /></span>
      <h1 className="font-display text-2xl font-black tracking-tight">Something went wrong</h1>
      <p className="max-w-full break-words font-mono text-sm text-muted">{message}</p>

      {state === "sent" ? (
        <p className="text-sm text-verified">Thanks — we got it. We've opened a support ticket and will follow up with you there.</p>
      ) : state === "failed" ? (
        <p className="text-sm text-muted">Couldn't send it automatically. Please reach us via <a href="/support" className="font-bold text-accent-strong">Support</a>.</p>
      ) : (
        <p className="text-sm text-muted">Send this to our team and we'll fix it and follow up with you.</p>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {state !== "sent" && <Button onClick={send} disabled={state === "sending"}>{state === "sending" ? "Sending…" : "Report this to us"}</Button>}
        <Button variant="ghost" onClick={() => location.reload()}>Reload</Button>
        <a href="/"><Button variant="ghost">Go home</Button></a>
      </div>
      {state === "sent" && <a href="/support" className="font-mono text-xs font-bold text-accent-strong">Track it in Support →</a>}
    </div>
  );
}
